// frontend/src/components/AGTFit/utils/AGTFloatingChat.js
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { Global, css } from '@emotion/react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  styled,
  alpha,
  InputBase,
  ClickAwayListener
} from '@mui/material';
import {
  Minimize2,
  ArrowUp,
  Dumbbell,
  Apple,
  Scale,
  LineChart,
  Camera
} from 'lucide-react';
import agtfitCoachAssistantService from '../service/agtfitCoachAssistantService';
import AppleSpinner from '../../common/AppleSpinner';
import ProMessageAlert from './ProMessageAlert';
import ProFeaturesModal from './ProFeaturesModal';
import CameraCapture from './CameraCapture';
import { useAGTFitProContext } from '../context/AGTFitProContext';

// ✅ FUNCIÓN PARA FILTRAR ACCIONES JSON EN EL FRONTEND
const filterJsonActions = (content) => {
  if (typeof content !== 'string') return content;
  
  // Patrones para detectar y remover acciones JSON
  const actionPatterns = [
    /\[MEAL_CREATE\].*?\[\/MEAL_CREATE\]/gs,
    /\[EXERCISE_CREATE\].*?\[\/EXERCISE_CREATE\]/gs,
    /\[WEIGHT_UPDATE\].*?\[\/WEIGHT_UPDATE\]/gs,
    /\[WATER_UPDATE\].*?\[\/WATER_UPDATE\]/gs,
    /\[MEAL_DELETE\].*?\[\/MEAL_DELETE\]/gs,
    /\[EXERCISE_DELETE\].*?\[\/EXERCISE_DELETE\]/gs,
    /\[MEAL_UPDATE\].*?\[\/MEAL_UPDATE\]/gs,
    /\[EXERCISE_UPDATE\].*?\[\/EXERCISE_UPDATE\]/gs,
    /\[WATER_DELETE\].*?\[\/WATER_DELETE\]/gs
  ];
  
  let filteredContent = content;
  let hasActions = false;
  
  // Remover todas las acciones JSON del contenido
  actionPatterns.forEach(pattern => {
    if (pattern.test(filteredContent)) {
      hasActions = true;
      filteredContent = filteredContent.replace(pattern, '').trim();
    }
  });
  
  // Si se detectaron acciones, agregar mensaje de confirmación
  if (hasActions && !filteredContent.includes('✅')) {
    filteredContent += '\n\n✅ ¡Registrado correctamente! Tu información ha sido actualizada.';
  }
  
  return filteredContent;
};

// ✅ FUNCIÓN PARA DETECTAR SI HAY ACCIONES JSON COMPLETAS
const hasCompleteJsonActions = (content) => {
  if (typeof content !== 'string') return false;
  
  const completionPatterns = [
    /\[\/MEAL_CREATE\]/g,
    /\[\/EXERCISE_CREATE\]/g,
    /\[\/WEIGHT_UPDATE\]/g,
    /\[\/WATER_UPDATE\]/g,
    /\[\/MEAL_DELETE\]/g,
    /\[\/EXERCISE_DELETE\]/g,
    /\[\/MEAL_UPDATE\]/g,
    /\[\/EXERCISE_UPDATE\]/g,
    /\[\/WATER_DELETE\]/g
  ];
  
  return completionPatterns.some(pattern => pattern.test(content));
};

// Estilos globales con efectos de vidrio líquido y animaciones
const GlobalStyles = () => (
  <Global
    styles={css`
      .liquid-glass-chat {
        backdrop-filter: blur(20px) saturate(180%);
        -webkit-backdrop-filter: blur(20px) saturate(180%);
      }
      
      .markdown {
        line-height: 1.43;
      }
      
      .glass-morphism {
        background: rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(20px) saturate(180%);
        -webkit-backdrop-filter: blur(20px) saturate(180%);
        border: 1px solid rgba(255, 255, 255, 0.2);
      }

      /* Keyframes para animación líquida */
      @keyframes liquidExpansion {
        0% {
          transform: translateX(-50%) scale(0.1);
          opacity: 0;
          border-radius: 50px;
        }
        30% {
          transform: translateX(-50%) scale(0.6);
          opacity: 0.7;
          border-radius: 35px;
        }
        60% {
          transform: translateX(-50%) scale(1.05);
          opacity: 0.9;
          border-radius: 28px;
        }
        80% {
          transform: translateX(-50%) scale(0.98);
          opacity: 1;
          border-radius: 25px;
        }
        100% {
          transform: translateX(-50%) scale(1);
          opacity: 1;
          border-radius: 24px;
        }
      }
    `}
  />
);

// Componente SVG para los filtros de deformación
const LiquidDeformationFilters = () => (
  <svg className="liquid-deformation-filter">
    <defs>
      {/* Filtro principal de deformación líquida */}
      <filter id="liquidDistortion" x="-50%" y="-50%" width="200%" height="200%">
        {/* Turbulencia para crear ondas */}
        <feTurbulence
          baseFrequency="0.02 0.03"
          numOctaves="3"
          result="turbulence"
          seed="5"
        />
        
        {/* Mapa de desplazamiento para crear la deformación */}
        <feDisplacementMap
          in="SourceGraphic"
          in2="turbulence"
          scale="8"
          xChannelSelector="R"
          yChannelSelector="G"
          result="displacement"
        />
        
        {/* Gaussian blur sutil para suavizar */}
        <feGaussianBlur in="displacement" stdDeviation="1" result="blur"/>
        
        {/* Saturación y contraste */}
        <feColorMatrix
          in="blur"
          type="saturate"
          values="1.8"
          result="saturated"
        />
        
        <feColorMatrix
          in="saturated"
          type="matrix"
          values="1.2 0 0 0 0
                  0 1.2 0 0 0
                  0 0 1.2 0 0
                  0 0 0 1 0"
          result="final"
        />
      </filter>

      {/* Filtro alternativo con ondas más pronunciadas */}
      <filter id="liquidWaves" x="-50%" y="-50%" width="200%" height="200%">
        <feTurbulence
          baseFrequency="0.015 0.025"
          numOctaves="4"
          result="turbulence"
          seed="10"
        />
        
        <feDisplacementMap
          in="SourceGraphic"
          in2="turbulence"
          scale="12"
          xChannelSelector="R"
          yChannelSelector="B"
          result="displacement"
        />
        
        <feGaussianBlur in="displacement" stdDeviation="0.5" result="blur"/>
        
        <feColorMatrix
          in="blur"
          type="saturate"
          values="2"
          result="final"
        />
      </filter>

      {/* Filtro sutil para menor deformación */}
      <filter id="liquidSubtle" x="-20%" y="-20%" width="140%" height="140%">
        <feTurbulence
          baseFrequency="0.03 0.02"
          numOctaves="2"
          result="turbulence"
          seed="3"
        />
        
        <feDisplacementMap
          in="SourceGraphic"
          in2="turbulence"
          scale="4"
          xChannelSelector="G"
          yChannelSelector="R"
          result="displacement"
        />
        
        <feGaussianBlur in="displacement" stdDeviation="1.5" result="final"/>
      </filter>
    </defs>
  </svg>
);

// Paleta de colores Apple actualizada para glassmorphismo
const appleColors = {
  light: {
    label: { primary: '#000000' },
    system: {
      grey: '#8E8E93',
      grey3: '#C7C7CC',
      green: '#31C859',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { 
      primaryElevated: 'rgba(255, 255, 255, 0.95)',
      glass: 'rgba(255, 255, 255, 0.7)',
      glassHover: 'rgba(255, 255, 255, 0.85)'
    },
    fill: { tertiary: '#767680' },
    ui: { accent: '#007AFF', base: 'rgba(242, 242, 247, 0.8)' }
  },
  dark: {
    label: { primary: '#FFFFFF' },
    system: {
      grey: '#8E8E93',
      grey3: '#464649',
      green: '#2DD257',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { 
      primaryElevated: 'rgba(0, 0, 0, 0.85)',
      glass: 'rgba(255, 255, 255, 0.1)',
      glassHover: 'rgba(255, 255, 255, 0.15)'
    },
    fill: { tertiary: '#767680' },
    ui: { accent: '#0A84FF', base: 'rgba(39, 39, 41, 0.8)' }
  }
};

const navigationColors = (theme) => {
  const mode = theme.palette.mode;
  const c = mode === 'light' ? appleColors.light : appleColors.dark;
  return {
    background: c.bg.primaryElevated,
    glass: c.bg.glass,
    glassHover: c.bg.glassHover,
    textDefault: c.label.primary,
    textSecondary: c.system.grey,
    cardBackground: c.ui.base,
    borderColor: mode === 'light' ? 'rgba(199, 199, 204, 0.3)' : 'rgba(70, 70, 73, 0.3)',
    accent: c.ui.accent,
    success: c.system.green,
    error: '#FF3B30',
    warning: '#FF9500'
  };
};

// Componente LiquidGlassButton integrado
const LiquidGlassButton = styled(IconButton)(({ theme, isactive = false }) => {
  const navColors = navigationColors(theme);
  const isDarkMode = theme.palette.mode === 'dark';
  
  return {
    minWidth: '32px',
    height: '32px',
    borderRadius: '50%',
    position: 'relative',
    border: 'none',
    outline: 'none',
    
    // Efecto de vidrio adaptado para modo oscuro/claro
    background: isDarkMode ? `
      radial-gradient(circle at 30% 30%, 
        rgba(255, 255, 255, ${isactive ? 0.25 : 0.15}) 0%, 
        rgba(255, 255, 255, ${isactive ? 0.15 : 0.08}) 45%,
        rgba(255, 255, 255, ${isactive ? 0.08 : 0.04}) 100%
      )
    ` : `
      radial-gradient(circle at 30% 30%, 
        rgba(255, 255, 255, ${isactive ? 0.95 : 0.8}) 0%, 
        rgba(255, 255, 255, ${isactive ? 0.7 : 0.5}) 45%,
        rgba(255, 255, 255, ${isactive ? 0.4 : 0.25}) 100%
      )
    `,
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    
    // Sistema de sombras mejorado
    boxShadow: isDarkMode ? `
      0 8px 32px rgba(0, 0, 0, 0.2),
      0 1px 2px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.2),
      inset 0 -1px 0 rgba(0, 0, 0, 0.1)
    ` : `
      0 8px 32px rgba(0, 0, 0, 0.08),
      0 1px 2px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.4),
      inset 0 -1px 0 rgba(0, 0, 0, 0.05)
    `,
    
    transition: theme.transitions.create(['transform', 'box-shadow'], {
      duration: theme.transitions.duration.short,
      easing: theme.transitions.easing.easeInOut
    }),
    
    '&:hover': {
      transform: 'scale(1)',
      boxShadow: isDarkMode ? `
        0 12px 40px rgba(0, 0, 0, 0.3),
        0 2px 4px rgba(0, 0, 0, 0.4),
        inset 0 1px 0 rgba(255, 255, 255, 0.3),
        inset 0 -1px 0 rgba(0, 0, 0, 0.1)
      ` : `
        0 12px 40px rgba(0, 0, 0, 0.12),
        0 2px 4px rgba(0, 0, 0, 0.15),
        inset 0 1px 0 rgba(255, 255, 255, 0.5),
        inset 0 -1px 0 rgba(0, 0, 0, 0.05)
      `,
    },
    
    '&:active': {
      transform: 'scale(0.95)',
    },
    
    color: navColors.textDefault
  };
});

// Botón de envío con gradiente y efecto liquid glass
const LiquidSendButton = styled(IconButton)(({ theme, hasvalue }) => {
  const isDarkMode = theme.palette.mode === 'dark';
  
  return {
    minWidth: '32px',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    padding: '10px',
    transition: theme.transitions.create(['transform', 'background', 'box-shadow'], {
      duration: theme.transitions.duration.short,
      easing: theme.transitions.easing.easeInOut
    }),
    
    background: hasvalue ? 
      'linear-gradient(135deg, #FF9800 0%, #FF6B00 50%, #FF5722 100%)' :
      isDarkMode ? `
        radial-gradient(circle at 30% 30%, 
          rgba(255, 255, 255, 0.15) 0%, 
          rgba(255, 255, 255, 0.08) 45%,
          rgba(255, 255, 255, 0.04) 100%
        )
      ` : `
        radial-gradient(circle at 30% 30%, 
          rgba(255, 255, 255, 0.8) 0%, 
          rgba(255, 255, 255, 0.5) 45%,
          rgba(255, 255, 255, 0.25) 100%
        )
      `,
    
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    
    boxShadow: hasvalue ? `
      0 4px 20px rgba(255, 87, 34, 0.4),
      0 1px 2px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.3)
    ` : isDarkMode ? `
      0 4px 16px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.1)
    ` : `
      0 4px 16px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.3)
    `,
    
    '&:hover': {
      transform: 'scale(1)',
      boxShadow: hasvalue ? `
        0 6px 24px rgba(255, 87, 34, 0.5),
        0 2px 4px rgba(0, 0, 0, 0.3),
        inset 0 1px 0 rgba(255, 255, 255, 0.4)
      ` : isDarkMode ? `
        0 6px 20px rgba(0, 0, 0, 0.3),
        inset 0 1px 0 rgba(255, 255, 255, 0.2)
      ` : `
        0 6px 20px rgba(0, 0, 0, 0.15),
        inset 0 1px 0 rgba(255, 255, 255, 0.4)
      `,
    },
    
    '&:active': {
      transform: 'scale(0.95)',
    },
    
    color: hasvalue ? 'white' : theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'
  };
});

// Contenedor principal del chat con animación líquida
const LiquidChatContainer = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isexpanding' && prop !== 'iscollapsing'
})(({ theme, isexpanding, iscollapsing }) => {
  // const navColors = navigationColors(theme); // Unused variable
  const isDarkMode = theme.palette.mode === 'dark';
  const isSmallScreen = useMediaQuery('(max-height: 700px)');
  
  return {
    borderRadius: '24px',
    width: '360px',
    height: isSmallScreen ? '450px' : '600px',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    transformOrigin: 'center bottom',
    
    // Efecto de vidrio líquido principal MEJORADO
    background: isDarkMode ? `
      radial-gradient(ellipse 200% 100% at 50% 0%, 
        rgba(255, 255, 255, 0.15) 0%, 
        rgba(255, 255, 255, 0.12) 20%,
        rgba(255, 255, 255, 0.08) 40%,
        rgba(255, 255, 255, 0.06) 60%,
        rgba(255, 255, 255, 0.04) 80%,
        rgba(255, 255, 255, 0.02) 100%
      ),
      linear-gradient(135deg, 
        rgba(255, 255, 255, 0.08) 0%,
        rgba(255, 255, 255, 0.04) 50%,
        rgba(255, 255, 255, 0.02) 100%
      )
    ` : `
      radial-gradient(ellipse 200% 100% at 50% 0%, 
        rgba(255, 255, 255, 0.95) 0%, 
        rgba(255, 255, 255, 0.85) 20%,
        rgba(255, 255, 255, 0.7) 40%,
        rgba(255, 255, 255, 0.6) 60%,
        rgba(255, 255, 255, 0.5) 80%,
        rgba(255, 255, 255, 0.4) 100%
      )
    `,
    
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    
    // Borde con efecto de desvanecimiento MEJORADO
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderRadius: 'inherit',
      background: isDarkMode ? `
        linear-gradient(45deg, 
          rgba(255, 255, 255, 0.2) 0%,
          transparent 25%,
          transparent 75%,
          rgba(255, 255, 255, 0.2) 100%
        ),
        linear-gradient(-45deg, 
          rgba(255, 255, 255, 0.15) 0%,
          transparent 25%,
          transparent 75%,
          rgba(255, 255, 255, 0.15) 100%
        )
      ` : `
        linear-gradient(45deg, 
          rgba(255, 255, 255, 0.4) 0%,
          transparent 25%,
          transparent 75%,
          rgba(255, 255, 255, 0.4) 100%
        ),
        linear-gradient(-45deg, 
          rgba(255, 255, 255, 0.3) 0%,
          transparent 25%,
          transparent 75%,
          rgba(255, 255, 255, 0.3) 100%
        )
      `,
      mask: 'linear-gradient(white, white) content-box, linear-gradient(white, white)',
      maskComposite: 'xor',
      WebkitMask: 'linear-gradient(white, white) content-box, linear-gradient(white, white)',
      WebkitMaskComposite: 'xor',
      padding: '1px',
      pointerEvents: 'none',
    },
    
    boxShadow: isDarkMode ? `
      0 20px 60px rgba(0, 0, 0, 0.4),
      0 8px 32px rgba(0, 0, 0, 0.3),
      0 1px 2px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.1)
    ` : `
      0 20px 60px rgba(0, 0, 0, 0.1),
      0 8px 32px rgba(0, 0, 0, 0.08),
      0 1px 2px rgba(0, 0, 0, 0.05),
      inset 0 1px 0 rgba(255, 255, 255, 0.3)
    `,
    
    transition: theme.transitions.create(['box-shadow'], {
      duration: theme.transitions.duration.complex,
      easing: theme.transitions.easing.easeInOut
    })
  };
});

// Input con efecto liquid glass + REFRACCIÓN SUAVE Y LIMPIA - MODIFICADO PARA MOSTRAR BOTONES
// ✅ VERSIÓN CON COMPATIBILIDAD SAFARI/iOS
const LiquidInputContainer = styled(Box)(({ theme, isexpanded }) => {
  // const navColors = navigationColors(theme); // Unused variable
  const isDarkMode = theme.palette.mode === 'dark';
  
  // ✅ DETECCIÓN DE SAFARI
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) || 
                   /iPad|iPhone|iPod/.test(navigator.userAgent);
  
  return {
    width: '360px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px 12px 20px',
    borderRadius: '50px',
    position: 'relative',
    cursor: isexpanded === 'true' ? 'default' : 'pointer',
    pointerEvents: 'auto',
    
    // Efecto de vidrio líquido para el input
    background: isDarkMode ? `
      radial-gradient(circle at 30% 30%, 
        rgba(255, 255, 255, 0.08) 0%, 
        rgba(255, 255, 255, 0.05) 40%,
        rgba(255, 255, 255, 0.03) 100%
      )
    ` : `
      radial-gradient(circle at 30% 30%, 
        rgba(200, 200, 200, 0.4) 0%, 
        rgba(220, 220, 220, 0.3) 40%,
        rgba(230, 230, 230, 0.25) 100%
      ),
      linear-gradient(135deg,
        rgba(240, 240, 240, 0.3) 0%,
        rgba(250, 250, 250, 0.2) 50%,
        rgba(245, 245, 245, 0.25) 100%
      )
    `,
    
    // ✅ BACKDROP-FILTER CONDICIONAL
    ...(isSafari ? {
      // 🍎 VERSIÓN SAFARI - Sin filtros SVG, más blur y efectos alternativos
      backdropFilter: `
        blur(12px) 
        saturate(140%) 
        contrast(115%) 
        brightness(104%)
        hue-rotate(2deg)
      `,
      WebkitBackdropFilter: `
        blur(12px) 
        saturate(140%) 
        contrast(115%) 
        brightness(104%)
        hue-rotate(2deg)
      `,
    } : {
      // 🌐 VERSIÓN OTROS NAVEGADORES - Con filtros SVG
      backdropFilter: `
        blur(1px) 
        saturate(120%) 
        contrast(110%) 
        brightness(102%)
        url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cfilter id='smoothGlass' x='-20%25' y='-20%25' width='140%25' height='140%25'%3E%3C!-- Noise base con frecuencia muy baja para suavidad --%3E%3CfeTurbulence baseFrequency='0.003 0.003' numOctaves='2' seed='3' stitchTiles='stitch' result='baseNoise'/%3E%3CfeGaussianBlur in='baseNoise' stdDeviation='12' result='smoothBase'/%3E%3C!-- Displacement muy suave y sutil --%3E%3CfeDisplacementMap in='SourceGraphic' in2='smoothBase' scale='8' xChannelSelector='R' yChannelSelector='G' result='gentleRefract'/%3E%3C!-- Segundo layer aún más suave --%3E%3CfeTurbulence baseFrequency='0.001 0.002' numOctaves='1' seed='7' stitchTiles='stitch' result='ultraSoftNoise'/%3E%3CfeGaussianBlur in='ultraSoftNoise' stdDeviation='20' result='ultraSmooth'/%3E%3CfeDisplacementMap in='gentleRefract' in2='ultraSmooth' scale='24' xChannelSelector='G' yChannelSelector='R' result='finalGlass'/%3E%3C!-- Blur final para eliminar cualquier artefacto --%3E%3CfeGaussianBlur in='finalGlass' stdDeviation='1' result='cleanGlass'/%3E%3C/filter%3E%3C/defs%3E%3C/svg%3E#smoothGlass")
      `,
      WebkitBackdropFilter: `
        blur(1px) 
        saturate(120%) 
        contrast(110%) 
        brightness(102%)
        url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cfilter id='smoothGlass' x='-20%25' y='-20%25' width='140%25' height='140%25'%3E%3C!-- Noise base con frecuencia muy baja para suavidad --%3E%3CfeTurbulence baseFrequency='0.003 0.003' numOctaves='2' seed='3' stitchTiles='stitch' result='baseNoise'/%3E%3CfeGaussianBlur in='baseNoise' stdDeviation='12' result='smoothBase'/%3E%3C!-- Displacement muy suave y sutil --%3E%3CfeDisplacementMap in='SourceGraphic' in2='smoothBase' scale='8' xChannelSelector='R' yChannelSelector='G' result='gentleRefract'/%3E%3C!-- Segundo layer aún más suave --%3E%3CfeTurbulence baseFrequency='0.001 0.002' numOctaves='1' seed='7' stitchTiles='stitch' result='ultraSoftNoise'/%3E%3CfeGaussianBlur in='ultraSoftNoise' stdDeviation='20' result='ultraSmooth'/%3E%3CfeDisplacementMap in='gentleRefract' in2='ultraSmooth' scale='24' xChannelSelector='G' yChannelSelector='R' result='finalGlass'/%3E%3C!-- Blur final para eliminar cualquier artefacto --%3E%3CfeGaussianBlur in='finalGlass' stdDeviation='1' result='cleanGlass'/%3E%3C/filter%3E%3C/defs%3E%3C/svg%3E#smoothGlass")
      `,
    }),
    
    // ✅ PSEUDO-ELEMENTO MEJORADO PARA SAFARI
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '0',
      left: '0',
      right: '0',
      bottom: '0',
      borderRadius: 'inherit',
      
      background: isSafari ? (
        // 🍎 FONDO MÁS PRONUNCIADO PARA SAFARI (compensa la falta de refracción)
        isDarkMode ? `
          radial-gradient(ellipse 120% 80% at 50% 50%, 
            rgba(255, 255, 255, 0.035) 0%,
            rgba(255, 255, 255, 0.015) 40%,
            transparent 70%
          ),
          radial-gradient(ellipse 200% 150% at 30% 30%, 
            rgba(255, 255, 255, 0.025) 0%,
            transparent 60%
          )
        ` : `
          radial-gradient(ellipse 120% 80% at 50% 50%, 
            rgba(200, 200, 200, 0.12) 0%,
            rgba(220, 220, 220, 0.06) 40%,
            transparent 70%
          ),
          radial-gradient(ellipse 200% 150% at 30% 30%, 
            rgba(240, 240, 240, 0.08) 0%,
            transparent 60%
          )
        `
      ) : (
        // 🌐 FONDO ORIGINAL PARA OTROS NAVEGADORES
        isDarkMode ? `
          radial-gradient(ellipse 120% 80% at 50% 50%, 
            rgba(255, 255, 255, 0.015) 0%,
            transparent 70%
          )
        ` : `
          radial-gradient(ellipse 120% 80% at 50% 50%, 
            rgba(200, 200, 200, 0.06) 0%,
            transparent 70%
          )
        `
      ),
      
      pointerEvents: 'none',
      zIndex: 1,
    },
    
    // Borde principal
    border: isDarkMode ? `1px solid rgba(255, 255, 255, 0.25)` : `1px solid rgba(255, 255, 255, 0.6)`,

    // Efecto inset lateral
    boxShadow: isDarkMode ? `
      0 8px 32px rgba(0, 0, 0, 0.3),
      0 1px 2px rgba(0, 0, 0, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.1),
      inset 0 0 1px rgba(255, 255, 255, 0.05),
      inset 1px 0 0 rgba(255, 255, 255, 0.15),
      inset -1px 0 0 rgba(255, 255, 255, 0.15)
    ` : `
      0 8px 32px rgba(0, 0, 0, 0.1),
      0 1px 2px rgba(0, 0, 0, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 0.4),
      inset 0 0 1px rgba(255, 255, 255, 0.2),
      inset 1px 0 0 rgba(255, 255, 255, 0.3),
      inset -1px 0 0 rgba(255, 255, 255, 0.3)
    `,
    
    // Pseudo-elemento para el borde degradado estilo iOS
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '-1px',
      left: '-1px',
      right: '-1px',
      bottom: '-1px',
      borderRadius: 'inherit',
      padding: '1px',
      
      background: isDarkMode ? `
        radial-gradient(ellipse at top, 
          rgba(255, 255, 255, 0.4) 0%,
          rgba(255, 255, 255, 0.35) 15%,
          rgba(255, 255, 255, 0.15) 40%,
          rgba(255, 255, 255, 0.08) 60%,
          rgba(255, 255, 255, 0.05) 80%,
          rgba(255, 255, 255, 0.15) 100%
        ),
        radial-gradient(ellipse at bottom, 
          rgba(255, 255, 255, 0.3) 0%,
          rgba(255, 255, 255, 0.25) 15%,
          rgba(255, 255, 255, 0.1) 40%,
          rgba(255, 255, 255, 0.05) 60%,
          rgba(255, 255, 255, 0.03) 80%,
          rgba(255, 255, 255, 0.1) 100%
        ),
        linear-gradient(90deg,
          rgba(255, 255, 255, 0.3) 0%,
          rgba(255, 255, 255, 0.1) 20%,
          rgba(255, 255, 255, 0.05) 50%,
          rgba(255, 255, 255, 0.1) 80%,
          rgba(255, 255, 255, 0.3) 100%
        )
      ` : `
        radial-gradient(ellipse at top, 
          rgba(255, 255, 255, 0.8) 0%,
          rgba(255, 255, 255, 0.7) 15%,
          rgba(255, 255, 255, 0.4) 40%,
          rgba(255, 255, 255, 0.25) 60%,
          rgba(255, 255, 255, 0.2) 80%,
          rgba(255, 255, 255, 0.4) 100%
        ),
        radial-gradient(ellipse at bottom, 
          rgba(255, 255, 255, 0.6) 0%,
          rgba(255, 255, 255, 0.5) 15%,
          rgba(255, 255, 255, 0.3) 40%,
          rgba(255, 255, 255, 0.2) 60%,
          rgba(255, 255, 255, 0.15) 80%,
          rgba(255, 255, 255, 0.3) 100%
        ),
        linear-gradient(90deg,
          rgba(255, 255, 255, 0.6) 0%,
          rgba(255, 255, 255, 0.3) 20%,
          rgba(255, 255, 255, 0.2) 50%,
          rgba(255, 255, 255, 0.3) 80%,
          rgba(255, 255, 255, 0.6) 100%
        )
      `,
      
      mask: `
        radial-gradient(ellipse, transparent 48%, black 50%),
        linear-gradient(black, black)
      `,
      WebkitMask: `
        radial-gradient(ellipse, transparent 48%, black 50%),
        linear-gradient(black, black)
      `,
      maskComposite: 'subtract',
      WebkitMaskComposite: 'subtract',
      pointerEvents: 'none',
      zIndex: 2,
    },
    
    transition: theme.transitions.create(['box-shadow', 'border'], {
      duration: theme.transitions.duration.complex,
      easing: theme.transitions.easing.easeInOut
    }),
    
    // Asegurar que el contenido esté por encima de los pseudo-elementos
    '& > *': {
      position: 'relative',
      zIndex: 3,
      cursor: isexpanded === 'true' ? 'text' : 'pointer'
    }
  };
});

// Tarjeta de prompt con efecto liquid glass
const LiquidPromptCard = styled(Box)(({ theme }) => {
  const navColors = navigationColors(theme);
  const isDarkMode = theme.palette.mode === 'dark';
  
  return {
    padding: theme.spacing(1, 1.5),
    borderRadius: '24px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flexShrink: 0,
    position: 'relative',
    
    background: isDarkMode ? `
      radial-gradient(circle at 30% 30%, 
        rgba(255, 255, 255, 0.12) 0%, 
        rgba(255, 255, 255, 0.08) 40%,
        rgba(255, 255, 255, 0.04) 100%
      )
    ` : `
      radial-gradient(circle at 30% 30%, 
        rgba(255, 255, 255, 0.8) 0%, 
        rgba(255, 255, 255, 0.6) 40%,
        rgba(255, 255, 255, 0.4) 100%
      )
    `,
    
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    
    boxShadow: isDarkMode ? `
      inset 0 1px 0 rgba(255, 255, 255, 0.3)
    ` : `
      0 4px 16px rgba(0, 0, 0, 0.04),
      inset 0 1px 0 rgba(255, 255, 255, 0.6)
    `,
    
    color: navColors.textDefault,
    
    transition: theme.transitions.create(['transform', 'box-shadow'], {
      duration: theme.transitions.duration.short,
      easing: theme.transitions.easing.easeInOut
    }),
  };
});

// ✅ COMPONENTE SIMPLE MARKDOWN ACTUALIZADO CON FILTRADO
const SimpleMarkdownMessage = ({ content }) => {
  // ✅ FILTRAR ACCIONES JSON ANTES DE RENDERIZAR
  const filteredContent = filterJsonActions(content);
  
  return (
    <ReactMarkdown
      components={{
        p: ({ children, ...props }) => (
          <p style={{ marginBottom: '1em' }}>
            {children}
          </p>
        ),
        strong: ({ children }) => <strong>{children}</strong>,
        em: ({ children }) => <em>{children}</em>,
        li: ({ children }) => (
          <li style={{ marginBottom: '0.5em' }}>
            {children}
          </li>
        ),
        h1: ({ children }) => (
          <h1 style={{ marginBottom: '0.75em', marginTop: '1em' }}>
            {children}
          </h1>
        ),
        h2: ({ children }) => (
          <h2 style={{ marginBottom: '0.75em', marginTop: '1em' }}>
            {children}
          </h2>
        ),
        pre: ({ children }) => (
          <pre style={{ marginBottom: '1em', marginTop: '0.5em', whiteSpace: 'pre-wrap' }}>
            {children}
          </pre>
        ),
        code: ({ inline, children, ...props }) => {
          if (inline) {
            return <code {...props}>{children}</code>;
          }
          return (
            <code {...props} style={{ display: 'block', whiteSpace: 'pre-wrap' }}>
              {children}
            </code>
          );
        },
        br: () => <br />
      }}
    >
      {filteredContent}
    </ReactMarkdown>
  );
};

const useLatestMessage = () => {
  const [latestMessageId, setLatestMessageId] = useState(null);
 
  const updateLatestMessage = useCallback((messages, specificId = null) => {
    if (specificId !== null) {
      setLatestMessageId(specificId);
      setTimeout(() => {
        setLatestMessageId(null);
      }, 100);
    } else {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage && lastMessage.type === 'assistant') {
        setLatestMessageId(messages.length - 1);
        setTimeout(() => {
          setLatestMessageId(null);
        }, 100);
      }
    }
  }, []);
 
  return { latestMessageId, updateLatestMessage };
};

// Input con espaciado corregido
const StyledInputBase = styled(InputBase)(({ theme, isexpanded }) => {
  const navColors = navigationColors(theme);
  return {
    width: '100%',
    color: navColors.textDefault,
    marginLeft: '4px', // Pequeño margen adicional para separarlo del borde
    '& .MuiInputBase-input': {
      padding: '0px',
      cursor: isexpanded === 'true' ? 'text' : 'pointer',
      '&::placeholder': {
        color: navColors.textSecondary,
        opacity: 1
      }
    }
  };
});

const ChatHeader = styled(Box)(({ theme }) => {
  // const navColors = navigationColors(theme); // Unused variable
  return {
    padding: theme.spacing(2),
    background: 'transparent',
    borderTopLeftRadius: '24px',
    borderTopRightRadius: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    zIndex: 1
  };
});

const MessageArea = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  flexGrow: 1,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  zIndex: 1,
  '&::-webkit-scrollbar': {
    width: 6,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: alpha(theme.palette.mode === 'light' ? '#000' : '#FFF', 0.4),
    borderRadius: 3,
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent'
  }
}));

const Message = styled(Box)(({ theme, variant, content }) => {
  const navColors = navigationColors(theme);
  const isDarkMode = theme.palette.mode === 'dark';

  const getMessageRadius = (messageContent) => {
    const contentLength = messageContent?.length || 0;
    if (contentLength < 20) {
      return '24px';
    } else if (contentLength < 50) {
      return '20px';
    } else {
      return '16px';
    }
  };

  const baseStyles = {
    maxWidth: '80%',
    padding: theme.spacing(1.5),
    borderRadius: getMessageRadius(content),
    color: navColors.textDefault,
    textTransform: 'none',
    position: 'relative'
  };

  if (variant === 'user') {
    return {
      ...baseStyles,
      alignSelf: 'flex-end',
      background: isDarkMode ? `
        radial-gradient(circle at 30% 30%, 
          rgba(255, 255, 255, 0.15) 0%, 
          rgba(255, 255, 255, 0.1) 45%,
          rgba(255, 255, 255, 0.05) 100%
        )
      ` : `
        radial-gradient(circle at 30% 30%, 
          rgba(255, 255, 255, 0.8) 0%, 
          rgba(255, 255, 255, 0.6) 45%,
          rgba(255, 255, 255, 0.4) 100%
        )
      `,
      backdropFilter: 'blur(10px) saturate(180%)',
      WebkitBackdropFilter: 'blur(10px) saturate(180%)',
      boxShadow: isDarkMode ? `
        0 4px 16px rgba(0, 0, 0, 0.2),
        inset 0 1px 0 rgba(255, 255, 255, 0.1)
      ` : `
        0 4px 16px rgba(0, 0, 0, 0.08),
        inset 0 1px 0 rgba(255, 255, 255, 0.3)
      `,
    };
  }

  return {
    ...baseStyles,
    alignSelf: 'flex-start',
    background: 'transparent',
    color: theme.palette.mode === 'light' ? '#000000' : '#FFFFFF',
  };
});

const PromptContainerMobile = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  marginBottom: theme.spacing(1),
  overflowX: 'auto',
  whiteSpace: 'nowrap',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  gap: theme.spacing(2),
  zIndex: 1,
  '&::-webkit-scrollbar': {
    display: 'none'
  }
}));

const PromptContainerDesktop = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
  zIndex: 1
}));

const FooterText = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  marginTop: theme.spacing(0.5),   // 🔹 Menos espacio arriba (era 1)
  marginBottom: theme.spacing(0.5), // 🔹 Menos espacio abajo
  fontSize: '0.55rem',             // 🔹 Texto más pequeño (era 0.65rem)
  color: '#808080',
  zIndex: 1
}));

const MarkdownContent = styled(Box)(({ theme }) => {
  const navColors = navigationColors(theme);
  return {
    fontSize: '0.875rem',
    lineHeight: 1.43,
    '& > *:first-of-type': {
      marginTop: 0
    },
    '& > *:last-child': {
      marginBottom: 0
    },
    '& p': {
      margin: '0 0 1em 0',
      color: navColors.textDefault,
      fontSize: '0.875rem',
      lineHeight: 1.43,
      whiteSpace: 'pre-line'
    },
    '& ul, & ol': {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1.5),
      paddingLeft: theme.spacing(2),
      '& li': {
        fontSize: '0.875rem',
        marginBottom: '0.5em',
        '&::marker': {
          fontSize: '0.875rem',
          color: navColors.textDefault
        }
      }
    },
    '& code': {
      backgroundColor: alpha(navColors.textDefault, 0.1),
      padding: '2px 4px',
      borderRadius: 4,
      fontSize: '0.875rem'
    },
    '& pre': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(navColors.textDefault, 0.15)
        : alpha(navColors.textDefault, 0.1),
      padding: theme.spacing(1),
      margin: theme.spacing(1, 0),
      borderRadius: 4,
      overflow: 'auto',
      whiteSpace: 'pre-wrap',
      '& code': {
        backgroundColor: 'transparent',
        padding: 0,
        fontSize: '0.875rem',
        color: theme.palette.mode === 'dark' ? '#e6e6e6' : 'inherit',
        whiteSpace: 'pre-wrap'
      }
    },
    '& blockquote': {
      borderLeft: `4px solid ${alpha(navColors.textDefault, 0.2)}`,
      margin: theme.spacing(1, 0),
      padding: theme.spacing(0.5, 1),
      fontSize: '0.875rem'
    },
    '& a': {
      color: navColors.accent,
      textDecoration: 'none',
      fontSize: '0.875rem',
      '&:hover': {
        textDecoration: 'underline'
      }
    },
    '& h1': {
      fontSize: '1.2rem',
      fontWeight: 600,
      margin: theme.spacing(1.5, 0, 1, 0)
    },
    '& h2': {
      fontSize: '1.1rem',
      fontWeight: 600,
      margin: theme.spacing(1.5, 0, 1, 0)
    },
    '& h3': {
      fontSize: '1rem',
      fontWeight: 600,
      margin: theme.spacing(1.2, 0, 0.8, 0)
    },
    '& hr': {
      margin: theme.spacing(1.5, 0),
      border: 0,
      borderTop: `1px solid ${alpha(navColors.textDefault, 0.2)}`
    },
    '& br': {
      display: 'block',
      content: '""',
      marginTop: '0.5em'
    },
    '& img': {
      maxWidth: '100%',
      height: 'auto',
      margin: theme.spacing(1, 0)
    }
  };
});

// ✅ FUNCIÓN PARA RENDERIZAR CONTENIDO CON FILTRADO
const renderMessageContent = (message, navColors, assistantTextColor) => {
  if (message.type === 'assistant') {
    return (
      <MarkdownContent className="markdown">
        <SimpleMarkdownMessage content={message.content} />
      </MarkdownContent>
    );
  }
 
  // Si el mensaje del usuario tiene una imagen, mostrarla
  if (message.type === 'user' && message.image) {
    return (
      <Box sx={{ width: '100%', maxWidth: '240px' }}>
        <img 
          src={message.image}
          alt="Captured food"
          style={{
            width: '100%',
            borderRadius: '8px',
            display: 'block'
          }}
        />
      </Box>
    );
  }
 
  return (
    <Typography 
      variant="body2" 
      sx={{ 
        color: assistantTextColor, 
        textTransform: 'none',
        whiteSpace: 'pre-line'
      }}
    >
      {message.content}
    </Typography>
  );
};

// Componente principal AGTFloatingChat ACTUALIZADO
const AGTFloatingChat = () => {
  const theme = useTheme();
  const navColors = navigationColors(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isExpanded, setIsExpanded] = useState(false);
  const [isExpanding, setIsExpanding] = useState(false);
  const [isCollapsing, setIsCollapsing] = useState(false);
  const [messages, setMessages] = useState([
    { 
      type: 'assistant', 
      content: '¡Hola! Soy AGT Fit, tu asistente de fitness. Puedo ayudarte a registrar comidas, ejercicios, controlar tu peso y mucho más. ¿En qué puedo ayudarte hoy?' 
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const messageAreaRef = useRef(null);
  const streamingImageMessageId = useRef(null);
  const inputRef = useRef(null);
  const shouldFocusRef = useRef(false);
  const { latestMessageId, updateLatestMessage } = useLatestMessage();
  const assistantTextColor = theme.palette.mode === 'light' ? '#000000' : '#FFFFFF';
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);
  
  // Estado para controlar el modal de funciones PRO y la cámara
  const [showProModal, setShowProModal] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  
  // Estado para rastrear si viene de CameraCapture
  const [expandedFromCamera, setExpandedFromCamera] = useState(false);

  // Estado para rastrear el ID del mensaje que se está construyendo con streaming
  const [streamingMessageId, setStreamingMessageId] = useState(null);

  // Contexto PRO
  const { isPro } = useAGTFitProContext();
  
  // Contador de mensajes del usuario
  const [userMessageCount, setUserMessageCount] = useState(0);
  
  // Bandera para mostrar alerta de límite de mensajes
  const [showMessageLimitAlert, setShowMessageLimitAlert] = useState(false);
  
  // Conversión del historial de mensajes al formato para la API
  const conversationHistory = useMemo(() => {
    return messages.map(msg => ({
      role: msg.type === 'user' ? 'user' : 'assistant',
      content: msg.content
    }));
  }, [messages]);

  // Función para hacer scroll hasta el final
  const scrollToBottom = useCallback(() => {
    if (messageAreaRef.current) {
      messageAreaRef.current.scrollTop = messageAreaRef.current.scrollHeight;
    }
  }, []);

  // Efecto para hacer autofocus en el input cuando se abre el chat
  useEffect(() => {
    if (isExpanded && inputRef.current && shouldFocusRef.current) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          inputRef.current.focus();
          shouldFocusRef.current = false;
        }, 50);
      });
    }
  }, [isExpanded]);

  // Efecto para hacer scroll al fondo cuando se abre el chat
  useEffect(() => {
    if (isExpanded && messageAreaRef.current && !expandedFromCamera) {
      scrollToBottom();
    }
  
    // Resetear la bandera después de procesar
    if (expandedFromCamera) {
      setExpandedFromCamera(false);
    }
  }, [isExpanded, scrollToBottom, expandedFromCamera]);

  // Efecto para realizar scroll solo cuando es necesario
  useEffect(() => {
    if (shouldScrollToBottom) {
      scrollToBottom();
      setShouldScrollToBottom(false);
    }
  }, [shouldScrollToBottom, scrollToBottom]);

  // Efecto para actualizar el último mensaje
  useEffect(() => {
    if (messages.length > 0) {
      updateLatestMessage(messages);
    }
  }, [messages, updateLatestMessage]);

  // Efecto para escuchar eventos de análisis completo (para CameraCapture)
  useEffect(() => {
    const handleAnalysisComplete = (event) => {
      const analysisResult = event.detail.message;
  
      if (streamingMessageId !== null) {
        // Actualizar el mensaje de análisis con el resultado real
        setMessages((prev) => {
          const newMessages = [...prev];
          if (newMessages[streamingMessageId]) {
            newMessages[streamingMessageId] = {
              ...newMessages[streamingMessageId],
              content: analysisResult
            };
          }
          return newMessages;
        });
  
        // Activar la animación en el mensaje actualizado
        updateLatestMessage([], streamingMessageId);
  
        // ✅ VERIFICAR SI HAY ACCIONES COMPLETAS Y DISPARAR EVENTO
        if (hasCompleteJsonActions(analysisResult)) {
          const checkmarkEvent = new CustomEvent('agtfit-checkmark-detected', {
            detail: { message: analysisResult }
          });
          document.dispatchEvent(checkmarkEvent);
        }
  
        // Limpiar el ID del mensaje de análisis
        setStreamingMessageId(null);
      }
    };
  
    // Añadir listener para el evento
    document.addEventListener('agtfit-analysis-complete', handleAnalysisComplete);
  
    // Limpiar listener al desmontar
    return () => {
      document.removeEventListener('agtfit-analysis-complete', handleAnalysisComplete);
    };
  }, [streamingMessageId, updateLatestMessage]);

  // Opciones de prompts para el asistente
  const promptOptions = [
    { 
      label: "Registrar comida", 
      icon: <Apple size={16} /> 
    },
    { 
      label: "Añadir ejercicio", 
      icon: <Dumbbell size={16} /> 
    },
    { 
      label: "Registrar peso", 
      icon: <Scale size={16} /> 
    },
    { 
      label: "Ver estadísticas", 
      icon: <LineChart size={16} /> 
    }
  ];

  // Función mejorada para manejar la expansión con animación líquida
  const handleExpand = (e) => {
    e.stopPropagation();
    if (!isExpanded && !isExpanding) {
      shouldFocusRef.current = true;
      setIsExpanding(true);
      setIsExpanded(true);
      
      // Resetear estado de expansión después de la animación
      setTimeout(() => {
        setIsExpanding(false);
      }, 600);
    }
  };

  // Función mejorada para manejar el colapso con animación líquida
  const handleCollapse = (e) => {
    e.stopPropagation();
    if (isExpanded && !isCollapsing) {
      setIsCollapsing(true);
      
      // Resetear estados después de la animación
      setTimeout(() => {
        setIsExpanded(false);
        setIsCollapsing(false);
      }, 400);
    }
  };

  // ✅ FUNCIÓN ACTUALIZADA PARA MANEJAR PROMPTS CON FILTRADO
  const handlePromptClick = async (promptText) => {
    if (isLoading) return;

    try {
      setIsLoading(true);
      
      // Incrementar contador de mensajes del usuario
      const newCount = userMessageCount + 1;
      setUserMessageCount(newCount);
      
      // Mostrar alerta después de 3 mensajes si no es PRO
      if (newCount >= 3 && !isPro) {
        setShowMessageLimitAlert(true);
      }

      // Añadir mensaje del usuario
      setMessages((prev) => [...prev, { type: 'user', content: promptText }]);
      setShouldScrollToBottom(true);

      // Crear mensaje placeholder del asistente para streaming
      let assistantMessageIndex;
      setMessages((prev) => {
        const newMessages = [...prev, { type: 'assistant', content: '' }];
        assistantMessageIndex = newMessages.length - 1;
        setStreamingMessageId(assistantMessageIndex);
        return newMessages;
      });

      // Activar identificación para el nuevo mensaje
      setTimeout(() => {
        updateLatestMessage([], assistantMessageIndex);
      }, 10);

      // Usar el servicio de streaming
      await agtfitCoachAssistantService.sendQueryStream(
        promptText,
        conversationHistory,
        // ✅ onToken: Añadir cada carácter al mensaje CON FILTRADO
        (token) => {
          setMessages((prev) => {
            const newMessages = [...prev];
            if (newMessages[assistantMessageIndex]) {
              const newContent = newMessages[assistantMessageIndex].content + token;
              
              // ✅ DETECTAR SI SE COMPLETÓ UNA ACCIÓN Y DISPARAR EVENTO
              if (hasCompleteJsonActions(newContent) && !hasCompleteJsonActions(newMessages[assistantMessageIndex].content)) {
                console.log('🎯 Acción JSON completa detectada, disparando evento checkmark');
                setTimeout(() => {
                  const checkmarkEvent = new CustomEvent('agtfit-checkmark-detected', {
                    detail: { message: newContent }
                  });
                  document.dispatchEvent(checkmarkEvent);
                }, 100);
              }
              
              newMessages[assistantMessageIndex] = {
                ...newMessages[assistantMessageIndex],
                content: newContent
              };
            }
            return newMessages;
          });
          setShouldScrollToBottom(true);
        },
        // ✅ onComplete mejorado
        (response) => {
          console.log('✅ Streaming completado para prompt');
          
          setIsLoading(false);
          setStreamingMessageId(null);
          
          // ✅ VERIFICAR CHECKMARKS EN EL CONTENIDO FINAL
          setMessages((prev) => {
            const currentMessage = prev[assistantMessageIndex];
            if (currentMessage && hasCompleteJsonActions(currentMessage.content)) {
              console.log('🎯 Checkmark detectado en mensaje final');
              setTimeout(() => {
                const checkmarkEvent = new CustomEvent('agtfit-checkmark-detected', {
                  detail: { message: currentMessage.content }
                });
                document.dispatchEvent(checkmarkEvent);
              }, 100);
            }
            return prev;
          });
        },
        // onError: Manejar errores
        (error) => {
          console.error('Error en streaming:', error);
          
          setIsLoading(false);
          setStreamingMessageId(null);
          
          setMessages((prev) => {
            const newMessages = [...prev];
            if (newMessages[assistantMessageIndex]) {
              newMessages[assistantMessageIndex] = {
                ...newMessages[assistantMessageIndex],
                content: 'Lo siento, ha ocurrido un error al procesar tu consulta. Por favor intenta más tarde.'
              };
            }
            return newMessages;
          });
        }
      );

    } catch (error) {
      console.error('Error general:', error);
      
      setIsLoading(false);
      setStreamingMessageId(null);
      
      setMessages((prev) => [
        ...prev,
        {
          type: 'assistant',
          content: 'Lo siento, ha ocurrido un error al procesar tu consulta. Por favor intenta más tarde.'
        }
      ]);
    }
  };

  // ✅ FUNCIÓN ACTUALIZADA PARA ENVIAR MENSAJES CON FILTRADO
  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const currentInput = inputValue.trim();
    
    try {
      setIsLoading(true);
      setInputValue('');
      
      // Incrementar contador de mensajes del usuario
      const newCount = userMessageCount + 1;
      setUserMessageCount(newCount);
      
      // Mostrar alerta después de 3 mensajes si no es PRO
      if (newCount >= 3 && !isPro) {
        setShowMessageLimitAlert(true);
      }

      // Añadir mensaje del usuario
      setMessages((prev) => [...prev, { type: 'user', content: currentInput }]);
      setShouldScrollToBottom(true);

      // Crear mensaje placeholder del asistente para streaming
      let assistantMessageIndex;
      setMessages((prev) => {
        const newMessages = [...prev, { type: 'assistant', content: '' }];
        assistantMessageIndex = newMessages.length - 1;
        setStreamingMessageId(assistantMessageIndex);
        return newMessages;
      });

      // Activar identificación para el nuevo mensaje
      setTimeout(() => {
        updateLatestMessage([], assistantMessageIndex);
      }, 10);

      // Usar el servicio de streaming
      await agtfitCoachAssistantService.sendQueryStream(
        currentInput,
        conversationHistory,
        // ✅ onToken: Añadir cada carácter al mensaje CON FILTRADO
        (token) => {
          setMessages((prev) => {
            const newMessages = [...prev];
            if (newMessages[assistantMessageIndex]) {
              const newContent = newMessages[assistantMessageIndex].content + token;
              
              // ✅ DETECTAR SI SE COMPLETÓ UNA ACCIÓN Y DISPARAR EVENTO
              if (hasCompleteJsonActions(newContent) && !hasCompleteJsonActions(newMessages[assistantMessageIndex].content)) {
                console.log('🎯 Acción JSON completa detectada, disparando evento checkmark');
                setTimeout(() => {
                  const checkmarkEvent = new CustomEvent('agtfit-checkmark-detected', {
                    detail: { message: newContent }
                  });
                  document.dispatchEvent(checkmarkEvent);
                }, 100);
              }
              
              newMessages[assistantMessageIndex] = {
                ...newMessages[assistantMessageIndex],
                content: newContent
              };
            }
            return newMessages;
          });
          setShouldScrollToBottom(true);
        },
        // ✅ onComplete mejorado
        (response) => {
          console.log('✅ Streaming completado para mensaje');
          
          setIsLoading(false);
          setStreamingMessageId(null);
          
          // ✅ VERIFICAR CHECKMARKS EN EL CONTENIDO FINAL
          setMessages((prev) => {
            const currentMessage = prev[assistantMessageIndex];
            if (currentMessage && hasCompleteJsonActions(currentMessage.content)) {
              console.log('🎯 Checkmark detectado en mensaje final');
              setTimeout(() => {
                const checkmarkEvent = new CustomEvent('agtfit-checkmark-detected', {
                  detail: { message: currentMessage.content }
                });
                document.dispatchEvent(checkmarkEvent);
              }, 100);
            }
            return prev;
          });
        },
        // onError: Manejar errores
        (error) => {
          console.error('Error en streaming:', error);
          
          setIsLoading(false);
          setStreamingMessageId(null);
          
          setMessages((prev) => {
            const newMessages = [...prev];
            if (newMessages[assistantMessageIndex]) {
              newMessages[assistantMessageIndex] = {
                ...newMessages[assistantMessageIndex],
                content: 'Lo siento, ha ocurrido un error al procesar tu consulta. Por favor intenta más tarde.'
              };
            }
            return newMessages;
          });
        }
      );

    } catch (error) {
      console.error('Error general:', error);
      
      setIsLoading(false);
      setStreamingMessageId(null);
      
      setMessages((prev) => [
        ...prev,
        {
          type: 'assistant',
          content: 'Lo siento, ha ocurrido un error al procesar tu consulta. Por favor intenta más tarde.'
        }
      ]);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  // Manejar clic en el botón de cámara
  const handleCameraClick = (e) => {
    e.stopPropagation();
    if (isPro) {
      setShowCamera(true);
    } else {
      setShowProModal(true);
    }
  };
  
  // ✅ FUNCIÓN CORREGIDA con useRef para handleImageAnalyzed
  const handleImageAnalyzed = (capturedImage, analysisText, streamingFlag) => {
    console.log('📸 Imagen analizada:', { 
      streamingFlag, 
      textLength: analysisText?.length, 
      token: analysisText?.substring(0, 20) + '...',
      currentStreamingId: streamingImageMessageId.current
    });
    
    if (streamingFlag === true) {
      // ✅ PRIMER TOKEN - Inicializar streaming en chat
      console.log('🚀 Iniciando streaming de imagen en chat');
      
      // Expandir el chat flotante inmediatamente
      if (!isExpanded) {
        setIsExpanded(true);
        setExpandedFromCamera(true);
      }
      
      // Incrementar contador de mensajes del usuario
      const newCount = userMessageCount + 1;
      setUserMessageCount(newCount);
      
      // Mostrar alerta después de 3 mensajes si no es PRO
      if (newCount >= 3 && !isPro) {
        setShowMessageLimitAlert(true);
      }
      
      // Añadir mensaje del usuario con la imagen capturada
      setMessages((prev) => [...prev, { 
        type: 'user', 
        content: "He tomado una foto de mi comida para analizar",
        image: capturedImage
      }]);
      
      // ✅ CREAR mensaje del asistente y GUARDAR EL ID en el useRef
      setMessages((prev) => {
        const newMessages = [...prev, { 
          type: 'assistant', 
          content: analysisText // ✅ Inicializar con el primer token
        }];
        const assistantMessageIndex = newMessages.length - 1;
        
        // ✅ GUARDAR en useRef (persistente) Y en state (para backward compatibility)
        streamingImageMessageId.current = assistantMessageIndex;
        setStreamingMessageId(assistantMessageIndex);
        
        console.log('💾 StreamingMessageId guardado:', assistantMessageIndex);
        
        return newMessages;
      });
      
      // Activar identificación para el nuevo mensaje
      setTimeout(() => {
        updateLatestMessage([], streamingImageMessageId.current);
      }, 10);
      
      setShouldScrollToBottom(true);
      
    } else if (streamingFlag === false) {
      // ✅ TOKEN ADICIONAL - Usar useRef para obtener el ID persistente
      const currentStreamingId = streamingImageMessageId.current;
      console.log(`🔤 Acumulando token: "${analysisText?.substring(0, 20)}..." (ID: ${currentStreamingId})`);
      
      if (currentStreamingId !== null) {
        setMessages((prev) => {
          const newMessages = [...prev];
          const messageToUpdate = newMessages[currentStreamingId];
          
          if (messageToUpdate && messageToUpdate.type === 'assistant') {
            // ✅ CONCATENAR el nuevo token al contenido existente
            const oldContent = messageToUpdate.content;
            const newContent = oldContent + analysisText;
            messageToUpdate.content = newContent;
            
            // ✅ DETECTAR SI SE COMPLETÓ UNA ACCIÓN Y DISPARAR EVENTO
            if (hasCompleteJsonActions(newContent) && !hasCompleteJsonActions(oldContent)) {
              console.log('🎯 Acción JSON completa detectada en imagen, disparando evento checkmark');
              setTimeout(() => {
                const checkmarkEvent = new CustomEvent('agtfit-checkmark-detected', {
                  detail: { message: newContent }
                });
                document.dispatchEvent(checkmarkEvent);
              }, 100);
            }
            
            console.log(`📝 Contenido actualizado:`, {
              oldLength: oldContent.length,
              newLength: messageToUpdate.content.length,
              addedToken: analysisText,
              preview: messageToUpdate.content.substring(0, 60) + '...'
            });
          } else {
            console.error('❌ Mensaje no encontrado o no es del asistente:', { currentStreamingId, messageToUpdate });
          }
          
          return newMessages;
        });
        
        setShouldScrollToBottom(true);
      } else {
        console.error('❌ streamingImageMessageId.current es null, no se puede actualizar mensaje');
      }
      
    } else if (streamingFlag === 'complete') {
      // ✅ STREAMING COMPLETADO
      console.log('✅ Streaming de imagen completado');
      
      const currentStreamingId = streamingImageMessageId.current;
      
      if (currentStreamingId !== null) {
        // Verificar si el mensaje final contiene un checkmark (✅)
        setMessages((prev) => {
          const currentMessage = prev[currentStreamingId];
          if (currentMessage && hasCompleteJsonActions(currentMessage.content)) {
            console.log('🎯 Checkmark detectado en mensaje final de imagen');
            const checkmarkEvent = new CustomEvent('agtfit-checkmark-detected', {
              detail: { message: currentMessage.content }
            });
            document.dispatchEvent(checkmarkEvent);
          }
          return prev;
        });
        
        // ✅ LIMPIAR AMBOS IDs
        streamingImageMessageId.current = null;
        setStreamingMessageId(null);
        console.log('🧹 StreamingMessageId limpiado (ambos)');
      }
      
    } else {
      // ✅ MODO LEGACY - Para compatibilidad con análisis sin streaming
      console.log('📸 Imagen analizada (modo legacy)');
      
      // Limpiar cualquier streaming activo
      streamingImageMessageId.current = null;
      setStreamingMessageId(null);
      
      // Expandir el chat si no está expandido
      if (!isExpanded) {
        setIsExpanded(true);
        setExpandedFromCamera(true);
      }
      
      // Incrementar contador de mensajes del usuario
      const newCount = userMessageCount + 1;
      setUserMessageCount(newCount);
      
      if (newCount >= 3 && !isPro) {
        setShowMessageLimitAlert(true);
      }
      
      // Añadir mensaje del usuario con imagen
      setMessages((prev) => [...prev, { 
        type: 'user', 
        content: "He tomado una foto de mi comida para analizar",
        image: capturedImage
      }]);
      
      // Añadir mensaje del asistente con el análisis
      setMessages((prev) => [...prev, { 
        type: 'assistant', 
        content: analysisText
      }]);
      
      setShouldScrollToBottom(true);
      
      // Verificar checkmarks
      if (hasCompleteJsonActions(analysisText)) {
        const checkmarkEvent = new CustomEvent('agtfit-checkmark-detected', {
          detail: { message: analysisText }
        });
        document.dispatchEvent(checkmarkEvent);
      }
    }
  };

  return (
    <>
      <GlobalStyles />
      <LiquidDeformationFilters />
      <Box
        sx={{
          position: 'fixed',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: theme.zIndex.drawer + 1
        }}
      >
        <ClickAwayListener onClickAway={handleCollapse}>
          <Box>
            {isExpanded && (
              <Box sx={{ marginBottom: 2 }}>
                <LiquidChatContainer 
                  className="liquid-glass-chat"
                  isexpanding={isExpanding}
                  iscollapsing={isCollapsing}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <ChatHeader>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Dumbbell size={20} style={{ marginRight: 8, color: '#FF7D00' }} />
                      <Typography
                        variant="subtitle1"
                        sx={{
                          textTransform: 'none',
                          color: navColors.textDefault,
                          fontWeight: 500
                        }}
                      >
                        AGT Fit {isPro && <span style={{ color: '#FF7D00', fontWeight: 600 }}>Pro</span>}
                      </Typography>
                    </Box>
                    <LiquidGlassButton
                      size="small"
                      onClick={handleCollapse}
                      sx={{
                        position: 'absolute',
                        right: theme.spacing(2)
                      }}
                    >
                      <Minimize2 size={18} />
                    </LiquidGlassButton>
                  </ChatHeader>

                  <MessageArea ref={messageAreaRef} onMouseDown={(e) => e.stopPropagation()}>
                    {messages.map((message, index) => (
                      <Message key={index} variant={message.type} content={message.content}>
                        {renderMessageContent(
                          message,
                          navColors,
                          assistantTextColor,
                          index === latestMessageId
                        )}
                      </Message>
                    ))}
                    {isLoading && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <Box sx={{ transform: 'scale(0.66)' }}>
                          <AppleSpinner />
                        </Box>
                      </Box>
                    )}
                    
                    {/* Alerta de límite de mensajes */}
                    {showMessageLimitAlert && !isPro && (
                      <ProMessageAlert onGetPro={() => setShowProModal(true)} />
                    )}
                    
                    <div ref={messagesEndRef} />
                  </MessageArea>

                  {messages.length === 1 &&
                    (isMobile ? (
                      <PromptContainerMobile onMouseDown={(e) => e.stopPropagation()}>
                        {promptOptions.map((option, idx) => (
                          <LiquidPromptCard
                            key={idx}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              handlePromptClick(option.label);
                            }}
                          >
                            {option.icon}
                            <Typography variant="body2" sx={{ textTransform: 'none' }}>
                              {option.label}
                            </Typography>
                          </LiquidPromptCard>
                        ))}
                      </PromptContainerMobile>
                    ) : (
                      <PromptContainerDesktop onMouseDown={(e) => e.stopPropagation()}>
                        {promptOptions.map((option, idx) => (
                          <LiquidPromptCard
                            key={idx}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              handlePromptClick(option.label);
                            }}
                          >
                            {option.icon}
                            <Typography variant="body2" sx={{ textTransform: 'none' }}>
                              {option.label}
                            </Typography>
                          </LiquidPromptCard>
                        ))}
                      </PromptContainerDesktop>
                    ))}

                  <Box sx={{ p: 1 }}>
                  </Box>

                  <FooterText>
                    AGT Fit te ayuda a alcanzar tus metas de fitness. Consulta a un profesional para decisiones importantes.
                  </FooterText>
                </LiquidChatContainer>
              </Box>
            )}

            {/* Input flotante con diseño liquid glass - MODIFICADO PARA MOSTRAR BOTONES SIEMPRE */}
            <LiquidInputContainer
              isexpanded={isExpanded.toString()}
              onMouseDown={!isExpanded ? handleExpand : undefined}
            >
              <StyledInputBase
                inputRef={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Pregunta a AGT Fit"
                disabled={!isExpanded}
                multiline
                maxRows={4}
                isexpanded={isExpanded.toString()}
                sx={{ 
                  pointerEvents: isExpanded ? 'auto' : 'none',
                  cursor: isExpanded ? 'text' : 'pointer'
                }}
              />
              
              {/* Contenedor de botones - SIEMPRE VISIBLE */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                {/* Botón de cámara con liquid glass - SIEMPRE VISIBLE */}
                <LiquidGlassButton
                  size="small"
                  onClick={handleCameraClick}
                >
                  <Camera size={18} />
                </LiquidGlassButton>
                
                {/* Botón de enviar/flecha - COMPORTAMIENTO CONDICIONAL */}
                {isExpanded ? (
                  <LiquidSendButton
                    hasvalue={inputValue.trim() ? 'true' : 'false'}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      handleSendMessage();
                    }}
                    disabled={!inputValue.trim() || isLoading}
                  >
                    <ArrowUp size={20} />
                  </LiquidSendButton>
                ) : (
                  <LiquidGlassButton
                    size="small"
                    onClick={handleExpand}
                  >
                    <ArrowUp size={18} />
                  </LiquidGlassButton>
                )}
              </Box>
            </LiquidInputContainer>
          </Box>
        </ClickAwayListener>
      </Box>
      
      {/* Modal de características PRO */}
      <ProFeaturesModal 
        open={showProModal} 
        onClose={() => setShowProModal(false)} 
      />
      
      {/* Componente de captura de cámara */}
      <CameraCapture
        open={showCamera}
        onClose={() => setShowCamera(false)}
        onImageAnalyzed={handleImageAnalyzed}
      />
    </>
  );
};

export default AGTFloatingChat;